import { Badge } from '@crowdfpl/ui-web/badge';
import { Button } from '@crowdfpl/ui-web/button';
import { Popover, PopoverContent, PopoverTrigger } from '@crowdfpl/ui-web/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@crowdfpl/ui-web/select';
import { Slider } from '@crowdfpl/ui-web/slider';
import { Swords, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { CreateDuelInlineButton } from '@/components/duels/CreateDuelInlineButton';
import { PlayerCombobox } from '@/components/duels/PlayerCombobox';
import { trackUmamiEvent } from '@/helpers/analytics';
import { cn } from '@/helpers/utils';
import { useCurrentSeason } from '@/hooks/data/seasons';
import { useTeams } from '@/hooks/data/teams';
import { useDuelsSearchLogic } from '@/hooks/useDuelsSearchLogic';
import type { PlayerDto } from '@/types/players';

const POSITIONS = [
  { value: 'GK', label: 'Goalkeepers' },
  { value: 'DEF', label: 'Defenders' },
  { value: 'MID', label: 'Midfielders' },
  { value: 'FWD', label: 'Forwards' },
] as const;

const toggleChip = (active: boolean): string =>
  cn(
    'inline-flex items-center shrink-0 h-8 px-3 text-xs font-medium rounded-full border transition-all cursor-pointer',
    active
      ? 'bg-secondary text-secondary-foreground border-secondary/80'
      : 'bg-transparent text-muted-foreground border-border/60 hover:border-border hover:text-foreground hover:bg-muted/40',
  );

interface DuelsSearchBarDesktopProps {
  selectedPosition: string;
  setSelectedPosition: (v: string) => void;
  selectedTeam: string;
  setSelectedTeam: (v: string) => void;
  onPlayersChange: (playerA: PlayerDto | null, playerB: PlayerDto | null) => void;
  selectedPlayerA: PlayerDto | null;
  selectedPlayerB: PlayerDto | null;
  setSelectedPlayerA: (player: PlayerDto | null) => void;
  setSelectedPlayerB: (player: PlayerDto | null) => void;
  gameweekId?: string;
  isNextWeek: boolean;
  existingDuelExists: boolean;
  newDuelsActive: boolean;
  onNewDuelsToggle: () => void;
  myFplTeamActive: boolean;
  onMyFplTeamToggle: () => void;
  priceRange: [number, number];
  priceMinMax: [number, number];
  onPriceRangeChange: (range: [number, number]) => void;
  onClearAll: () => void;
}

export function DuelsSearchBarDesktop({
  selectedPosition,
  setSelectedPosition,
  selectedTeam,
  setSelectedTeam,
  onPlayersChange,
  selectedPlayerA,
  selectedPlayerB,
  setSelectedPlayerA,
  setSelectedPlayerB,
  gameweekId,
  isNextWeek,
  existingDuelExists,
  newDuelsActive,
  onNewDuelsToggle,
  myFplTeamActive,
  onMyFplTeamToggle,
  priceRange,
  priceMinMax,
  onPriceRangeChange,
  onClearAll,
}: DuelsSearchBarDesktopProps): React.ReactElement {
  const { playerA, playerB, handlePlayerAChange, handlePlayerBChange, handleReset } = useDuelsSearchLogic({
    onFiltersChange: (_q, a, b) => onPlayersChange(a, b),
    selectedPlayerA,
    selectedPlayerB,
  });

  const [openPlayerA, setOpenPlayerA] = useState(false);
  const [openPlayerB, setOpenPlayerB] = useState(false);
  const [forceOpenB, setForceOpenB] = useState(false);

  const { data: seasonData } = useCurrentSeason();
  const { data: teamsData } = useTeams({ season_id: seasonData?.id, limit: 100 });

  const isPriceFiltered = priceRange[0] !== priceMinMax[0] || priceRange[1] !== priceMinMax[1];
  const [priceSliderValue, setPriceSliderValue] = useState<[number, number]>(priceRange);
  const [priceRangeMin, priceRangeMax] = priceRange;
  useEffect(() => {
    setPriceSliderValue([priceRangeMin, priceRangeMax]);
  }, [priceRangeMin, priceRangeMax]);

  const helperText = useMemo(() => {
    if (!isNextWeek) return 'Select players to find duels';
    const hasOnePlayer = (playerA && !playerB) || (!playerA && playerB);
    if (hasOnePlayer) return 'Select a second player to create or find duel';
    return 'Select players to create or find duels';
  }, [playerA, playerB, isNextWeek]);

  const handlePlayerASelected = (player: PlayerDto | null) => {
    handlePlayerAChange(player);
    setSelectedPlayerA(player);
    onPlayersChange(player, selectedPlayerB);
  };

  const handlePlayerAChangeWithAutoOpen = (player: PlayerDto | null) => {
    handlePlayerASelected(player);
    if (player && !playerB) {
      setTimeout(() => setForceOpenB(true), 150);
    }
  };

  const handlePlayerBSelected = (player: PlayerDto | null) => {
    handlePlayerBChange(player);
    setSelectedPlayerB(player);
    onPlayersChange(selectedPlayerA, player);
  };

  const handleClearAllClick = () => {
    handleReset();
    setSelectedPlayerA(null);
    setSelectedPlayerB(null);
    onPlayersChange(null, null);
    onClearAll();
  };

  const hasActiveFilters =
    selectedTeam ||
    selectedPosition ||
    selectedPlayerA ||
    selectedPlayerB ||
    newDuelsActive ||
    myFplTeamActive ||
    isPriceFiltered;

  return (
    <div className="flex flex-col gap-3">
      {/* Одна подложка на ширину родителя (container): игроки + подсказка + фильтры */}
      <div className="w-full rounded-lg border border-border/30 bg-muted/30 p-3">
        <div className="flex flex-col gap-3">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <PlayerCombobox
                id="playerA"
                placeholder="Player A"
                className="w-[clamp(120px,15vw,260px)] shrink-0"
                value={playerA}
                onChange={handlePlayerAChangeWithAutoOpen}
                excludePlayer={playerB || undefined}
                open={openPlayerA}
                onOpenChange={setOpenPlayerA}
              />
              <span className="-mx-1 shrink-0 text-sm font-medium text-muted-foreground">vs</span>
              <PlayerCombobox
                id="playerB"
                placeholder="Player B"
                className="w-[clamp(120px,15vw,260px)] shrink-0"
                value={playerB}
                onChange={handlePlayerBSelected}
                excludePlayer={playerA || undefined}
                open={openPlayerB}
                onOpenChange={(open) => {
                  setOpenPlayerB(open);
                  if (!open) setForceOpenB(false);
                }}
                forceOpen={forceOpenB}
              />
              <CreateDuelInlineButton
                playerA={selectedPlayerA}
                playerB={selectedPlayerB}
                gameweekId={gameweekId}
                isNextWeek={isNextWeek}
                existingDuelExists={existingDuelExists}
                onOpenPlayerA={() => setOpenPlayerA(true)}
                onOpenPlayerB={() => {
                  setForceOpenB(true);
                  setOpenPlayerB(true);
                }}
              />
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Swords className="h-3 w-3 shrink-0" />
              <span>{helperText}</span>
            </div>
          </div>

          {(playerA || playerB) && (
            <div className="flex flex-wrap items-center gap-2">
              {playerA && (
                <Badge
                  variant="secondary"
                  className="h-6 cursor-pointer gap-1 px-2.5 text-xs hover:bg-secondary/80"
                  onClick={() => {
                    handlePlayerAChange(null);
                    setSelectedPlayerA(null);
                    onPlayersChange(null, selectedPlayerB);
                  }}
                >
                  {playerA.web_name} <X className="h-2.5 w-2.5" />
                </Badge>
              )}
              {playerB && (
                <Badge
                  variant="secondary"
                  className="h-6 cursor-pointer gap-1 px-2.5 text-xs hover:bg-secondary/80"
                  onClick={() => {
                    handlePlayerBChange(null);
                    setSelectedPlayerB(null);
                    onPlayersChange(selectedPlayerA, null);
                  }}
                >
                  {playerB.web_name} <X className="h-2.5 w-2.5" />
                </Badge>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 border-t border-border/30 pt-3">
        <Select
          value={selectedPosition || 'all'}
          onValueChange={(v) => {
            trackUmamiEvent('duels_filter_change', { type: 'position', value: v === 'all' ? 'all' : v });
            setSelectedPosition(v === 'all' ? '' : v);
          }}
        >
          <SelectTrigger className="h-8 w-auto min-w-[110px] shrink-0 rounded-full border-border/60 text-xs">
            <SelectValue placeholder="All Positions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Positions</SelectItem>
            {POSITIONS.map((p) => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={selectedTeam || 'all'}
          onValueChange={(v) => {
            trackUmamiEvent('duels_filter_change', { type: 'team', value: v === 'all' ? 'all' : v });
            setSelectedTeam(v === 'all' ? '' : v);
          }}
        >
          <SelectTrigger className="h-8 w-auto min-w-[100px] shrink-0 rounded-full border-border/60 text-xs">
            <SelectValue placeholder="All Teams" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Teams</SelectItem>
            {teamsData?.teams.map((team) => (
              <SelectItem key={team.id} value={team.short_name}>{team.short_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Popover>
          <PopoverTrigger asChild>
            <button type="button" className={toggleChip(isPriceFiltered)}>
              £{(priceRange[0] / 10).toFixed(1)}–{(priceRange[1] / 10).toFixed(1)}m
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-4" align="start">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">Price range</span>
                <span className="text-xs text-muted-foreground">
                  £{(priceSliderValue[0] / 10).toFixed(1)}m – £{(priceSliderValue[1] / 10).toFixed(1)}m
                </span>
              </div>
              <div className="px-2">
                <Slider
                  min={priceMinMax[0]}
                  max={priceMinMax[1]}
                  step={1}
                  value={priceSliderValue}
                  onValueChange={(v) => setPriceSliderValue(v as [number, number])}
                  onValueCommit={(v) => onPriceRangeChange(v as [number, number])}
                />
              </div>
              <div className="flex justify-between text-[10px] text-muted-foreground">
                <span>£{(priceMinMax[0] / 10).toFixed(1)}m</span>
                <span>£{(priceMinMax[1] / 10).toFixed(1)}m</span>
              </div>
              {isPriceFiltered && (
                <Button variant="ghost" size="sm" className="w-full h-7 text-xs cursor-pointer" onClick={() => {
                  setPriceSliderValue(priceMinMax);
                  onPriceRangeChange(priceMinMax);
                }}>
                  Reset price
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>

        <div className="mx-1 h-5 w-px shrink-0 bg-border/50" aria-hidden />

        <button type="button" className={toggleChip(newDuelsActive)} onClick={onNewDuelsToggle}>
          New (24h)
        </button>

        <button type="button" className={toggleChip(myFplTeamActive)} onClick={onMyFplTeamToggle}>
          My FPL Team
        </button>

        {hasActiveFilters && (
          <button
            type="button"
            onClick={handleClearAllClick}
            className="ml-auto text-xs text-muted-foreground transition-colors hover:text-foreground cursor-pointer"
          >
            Clear all
          </button>
        )}
          </div>
        </div>
      </div>

      {/* Active filter badges */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-1 items-center">
          {newDuelsActive && (
            <Badge variant="secondary" className="gap-1 text-xs h-6 cursor-pointer hover:bg-secondary/80" onClick={onNewDuelsToggle}>
              New (24h) <X className="h-2 w-2" />
            </Badge>
          )}
          {myFplTeamActive && (
            <Badge variant="secondary" className="gap-1 text-xs h-6 cursor-pointer hover:bg-secondary/80" onClick={onMyFplTeamToggle}>
              My FPL Team <X className="h-2 w-2" />
            </Badge>
          )}
          {isPriceFiltered && (
            <Badge variant="secondary" className="gap-1 text-xs h-6 cursor-pointer hover:bg-secondary/80" onClick={() => onPriceRangeChange(priceMinMax)}>
              £{(priceRange[0] / 10).toFixed(1)}–{(priceRange[1] / 10).toFixed(1)}m <X className="h-2 w-2" />
            </Badge>
          )}
          {selectedPosition && (
            <Badge variant="secondary" className="gap-1 text-xs h-6 cursor-pointer hover:bg-secondary/80" onClick={() => setSelectedPosition('')}>
              {selectedPosition} <X className="h-2 w-2" />
            </Badge>
          )}
          {selectedTeam && (
            <Badge variant="secondary" className="gap-1 text-xs h-6 cursor-pointer hover:bg-secondary/80" onClick={() => setSelectedTeam('')}>
              {selectedTeam} <X className="h-2 w-2" />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
